import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Download, Share2, Loader2, ArrowLeft, Megaphone, MessageCircle, Palette, ToyBrick, Clapperboard, ImagePlus, X } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { NeonBackground } from "../components/NeonBackground";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";

const STYLES = [
  { key: "neon", label: "Neon", icon: Sparkles },
  { key: "normal", label: "Pubblicità", icon: Megaphone },
  { key: "cartoon", label: "Cartoon", icon: Palette },
  { key: "funko", label: "Funko Pop", icon: ToyBrick },
  { key: "pixar", label: "Pixar 3D", icon: Clapperboard },
];

export default function CreatePosterPage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [theme, setTheme] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [showHome, setShowHome] = useState(true);
  const [refs, setRefs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingStyle, setLoadingStyle] = useState(null);
  const [result, setResult] = useState(null);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - refs.length);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ path: r.data.path, preview: URL.createObjectURL(f) });
      }
      setRefs((prev) => [...prev, ...uploaded]);
    } catch {
      toast.error("Caricamento foto non riuscito");
    } finally {
      setUploading(false);
    }
  };

  const generate = async (style) => {
    if (!title.trim()) { toast.error("Inserisci un titolo"); return; }
    setLoading(true);
    setLoadingStyle(style);
    setResult(null);
    try {
      const r = await api.post("/promotions", {
        title, description, theme, show_on_home: showHome,
        expires_at: expiresAt || null, style, reference_paths: refs.map((x) => x.path),
      });
      setResult(r.data);
      toast.success("Locandina generata!");
    } catch {
      toast.error("Generazione non riuscita. Riprova.");
    } finally {
      setLoading(false);
      setLoadingStyle(null);
    }
  };

  const download = async () => {
    const res = await fetch(fileUrl(result.image_path));
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(title || "locandina").replace(/\s+/g, "-")}.png`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const share = async () => {
    const url = fileUrl(result.image_path);
    try {
      if (navigator.share) await navigator.share({ title, text: description, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copiato"); }
    } catch {}
  };

  const shareWhatsApp = () => {
    const url = fileUrl(result.image_path);
    const msg = `Ciao! 👋 Nuova promozione da *AC Creator 3D*:\n\n*${title}*${description ? "\n" + description : ""}\n\n👉 ${url}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <NeonBackground />
      <Navbar />
      <main className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <Link to="/promozioni" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white mb-6"><ArrowLeft className="w-4 h-4" /> Promozioni</Link>
        <p className="font-mono text-xs uppercase tracking-widest text-neon-cyan flex items-center gap-2"><Sparkles className="w-4 h-4" /> Solo amministratore</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-white mt-2 mb-8">Crea Locandina</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-5 bg-card border border-white/10 rounded-sm p-6">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Titolo</label>
              <Input data-testid="poster-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Sconto 20% sulle stampe 3D" className="bg-input border-white/10 rounded-sm mt-1" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Descrizione</label>
              <Textarea data-testid="poster-desc" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Descrivi la promozione: cosa offri, a chi, fino a quando…" className="bg-input border-white/10 rounded-sm mt-1 resize-none" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Tema / stile (facoltativo)</label>
              <Input data-testid="poster-theme" value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="Es. natalizio, minimal, colorato…" className="bg-input border-white/10 rounded-sm mt-1" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Scadenza (facoltativa)</label>
              <Input type="date" data-testid="poster-expiry" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="bg-input border-white/10 rounded-sm mt-1" />
              <p className="text-[11px] text-muted-foreground font-light mt-1">Dopo questa data la locandina sparisce automaticamente dalla Home.</p>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Foto di riferimento (facoltative)</label>
              <p className="text-[11px] text-muted-foreground font-light mt-0.5">Carica fino a 3 foto da usare come spunto o da inserire nella locandina (persone, oggetti, loghi…).</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {refs.map((r, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-sm overflow-hidden border border-white/15">
                    <img src={r.preview} alt="ref" data-testid={`poster-ref-thumb-${i}`} className="w-full h-full object-cover" />
                    <button data-testid={`poster-ref-remove-${i}`} onClick={() => setRefs(refs.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/70 p-0.5 hover:bg-neon-pink/80"><X className="w-3 h-3 text-white" /></button>
                  </div>
                ))}
                {refs.length < 3 && (
                  <label data-testid="poster-ref-add" className="w-16 h-16 rounded-sm border border-dashed border-white/25 flex items-center justify-center cursor-pointer hover:border-neon-cyan/50 transition-colors">
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin text-neon-cyan" /> : <ImagePlus className="w-5 h-5 text-muted-foreground" />}
                    <input data-testid="poster-ref-input" type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
                  </label>
                )}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-muted-foreground"><Switch data-testid="poster-home" checked={showHome} onCheckedChange={setShowHome} /> Mostra subito in Home</label>

            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Scegli lo stile e genera</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                {STYLES.map((s) => {
                  const Icon = s.icon;
                  const tid = s.key === "neon" ? "poster-generate" : `poster-generate-${s.key}`;
                  return (
                    <Button key={s.key} data-testid={tid} onClick={() => generate(s.key)} disabled={loading}
                      className={`rounded-sm gap-2 justify-center ${s.key === "neon" ? "bg-neon-cyan text-black hover:bg-white" : "bg-input text-white border border-white/15 hover:border-neon-cyan/50"} transition-colors`}>
                      {loadingStyle === s.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} {s.label}
                    </Button>
                  );
                })}
              </div>
            </div>
            <p className="text-xs text-muted-foreground font-light">L'immagine viene creata automaticamente dall'IA nello stile scelto, in base al testo e alle foto caricate. Titolo e descrizione vengono poi sovrapposti in modo nitido.</p>
          </div>

          <div className="bg-card border border-white/10 rounded-sm p-6 flex flex-col items-center justify-center min-h-[480px]">
            {loading ? (
              <div className="flex flex-col items-center text-muted-foreground"><Loader2 className="w-10 h-10 animate-spin text-neon-cyan mb-4" /><p className="font-light">Sto creando la tua locandina…</p></div>
            ) : result ? (
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="w-full">
                <img src={fileUrl(result.image_path)} alt={result.title} data-testid="poster-preview" className="w-full max-w-sm mx-auto rounded-sm border border-white/10" />
                <div className="flex flex-wrap items-center gap-2 mt-5 justify-center">
                  <Button data-testid="poster-download" onClick={download} variant="outline" className="rounded-sm gap-2 border-white/15"><Download className="w-4 h-4" /> Scarica</Button>
                  <Button data-testid="poster-share" onClick={share} variant="outline" className="rounded-sm gap-2 border-white/15"><Share2 className="w-4 h-4" /> Condividi</Button>
                  <Button data-testid="poster-whatsapp" onClick={shareWhatsApp} className="rounded-sm gap-2 bg-[#25D366] text-black hover:bg-[#1ebe5b]"><MessageCircle className="w-4 h-4" /> WhatsApp</Button>
                  <Button data-testid="poster-goto" onClick={() => navigate("/promozioni")} className="rounded-sm gap-2 bg-neon-pink text-white"><Megaphone className="w-4 h-4" /> Promozioni</Button>
                </div>
              </motion.div>
            ) : (
              <div className="flex flex-col items-center text-muted-foreground text-center"><Sparkles className="w-10 h-10 opacity-40 mb-3" /><p className="font-light">L'anteprima della locandina apparirà qui.</p></div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
